
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Info, Save, CheckCircle, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function EmailListIdSettings() {
  const [listId, setListId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { toast } = useToast();

  // Functie om de huidige waarde van ActiveCampaign lijst ID op te halen
  const fetchCurrentListId = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.functions.invoke("add-contact-to-activecampaign", {
        body: { action: "getListId" }
      });

      if (error) {
        console.error("Error fetching list ID:", error);
        toast({
          title: "Fout bij ophalen lijst ID",
          description: "Er is een fout opgetreden bij het ophalen van de lijst ID",
          variant: "destructive",
        });
        return;
      }

      if (data && data.listId) {
        setListId(data.listId);
        setIsSaved(true);
      }
    } catch (error) {
      console.error("Failed to fetch list ID:", error);
      toast({
        title: "Fout bij ophalen lijst ID",
        description: "Er is een fout opgetreden bij het ophalen van de lijst ID",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentListId();
  }, []);

  const saveListId = async () => {
    setIsLoading(true);
    setIsSaved(false);

    try {
      // Voer een test uit om te controleren of de lijst ID geldig is
      const { data, error } = await supabase.functions.invoke("add-contact-to-activecampaign", {
        body: { 
          action: "testListId", 
          listId 
        }
      });

      if (error) {
        toast({
          title: "Fout bij testen van lijst ID",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // Refresh de lijst ID na succesvol opslaan
      await fetchCurrentListId();

      toast({
        title: "Lijst ID opgeslagen",
        description: "De ActiveCampaign lijst ID is succesvol opgeslagen.",
      });
      
      setIsSaved(true);
    } catch (error) {
      toast({
        title: "Fout bij opslaan",
        description: "Er is een fout opgetreden bij het opslaan van de lijst ID.",
        variant: "destructive",
      });
      console.error("Error saving list ID:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openActiveCampaignLists = () => {
    window.open("https://mbbeterturne.activehosted.com/app/contacts", "_blank");
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>ActiveCampaign Lijst Instellingen</CardTitle>
        <CardDescription>
          Configureer de ActiveCampaign lijst waar nieuwe contacten aan worden toegevoegd bij registratie
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Over ActiveCampaign Integratie</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              De ActiveCampaign lijst ID is nodig om nieuwe gebruikers automatisch 
              aan een specifieke lijst toe te voegen bij registratie.
            </p>
            <p className="mb-2">
              Bij registratie worden de volgende gegevens naar ActiveCampaign verstuurd:
            </p>
            <ul className="list-disc list-inside mb-2">
              <li>Naam (gesplitst in voornaam en achternaam)</li>
              <li>E-mailadres</li>
            </ul>
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={openActiveCampaignLists}
                className="flex items-center"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Bekijk lijsten in ActiveCampaign
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <label htmlFor="listId" className="text-sm font-medium">
            ActiveCampaign Lijst ID
          </label>
          <div className="flex space-x-2">
            <Input
              id="listId"
              placeholder="Voer lijst ID in (bijv. 76)"
              value={listId}
              onChange={(e) => {
                setListId(e.target.value);
                setIsSaved(false);
              }}
              className="flex-1"
            />
            <Button 
              onClick={saveListId} 
              disabled={isLoading || !listId}
            >
              {isLoading ? (
                "Opslaan..."
              ) : (
                <>
                  {isSaved ? <CheckCircle className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                  Opslaan
                </>
              )}
            </Button>
          </div>
        </div>

        <Alert className="mt-4 bg-amber-50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Belangrijk</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              Zorg ervoor dat de ActiveCampaign API key correct is ingesteld in de 
              Supabase project settings voordat je de lijst ID configureert.
            </p>
            <p>
              Je kunt de lijst ID vinden in de URL wanneer je de lijst bekijkt in ActiveCampaign.
              Voorbeeld: https://mbbeterturne.activehosted.com/app/contacts?listid=<strong>76</strong>
              (in dit geval is 76 de lijst ID)
            </p>
          </AlertDescription>
        </Alert>
      </CardContent>
      <CardFooter className="text-xs text-muted-foreground">
        <p>
          Wijzigen van deze instelling heeft alleen effect op nieuwe registraties.
        </p>
      </CardFooter>
    </Card>
  );
}
